// Shared helpers for the admin's PDFs (order invoices and client invoices): loading jsPDF and the Geist typeface on first use, and
// rasterising the logo. The libraries are self-hosted (public/vendor), so the admin never runs third-party scripts.

// shadcn/ui "zinc" tokens as RGB for jsPDF, the same visual language as the admin and the emails.
export const FG = [9, 9, 11];            // --foreground
export const MUTED_FG = [113, 113, 122]; // --muted-foreground
export const BORDER = [228, 228, 231];   // --border
export const MUTED = [244, 244, 245];    // --muted / --secondary
export const PRIMARY = [24, 24, 27];     // --primary
export const RADIUS = 2.5;               // mm, about --radius (0.5rem)

// (A <script> inside <svelte:head> is inserted without being executed, which is why this builds the element by hand.)
const loadScript = (src) => new Promise((resolve, reject) => {
  const el = document.createElement('script');
  el.src = src;
  el.onload = resolve;
  el.onerror = () => reject(new Error(`Could not load ${src}`));
  document.head.appendChild(el);
});
let pdfLibs = null;
export function loadPdfLibs() {
  if (window.jspdf?.jsPDF?.API?.autoTable) return Promise.resolve();
  pdfLibs ??= loadScript('/vendor/jspdf.umd.min.js')
    .then(() => loadScript('/vendor/jspdf.plugin.autotable.min.js'))
    .catch((e) => { pdfLibs = null; throw e; });
  return pdfLibs;
}

// Geist (the admin's typeface, SIL OFL) is embedded so the PDF matches the UI; if the files can't be fetched the PDF falls back to Helvetica.
const GEIST = [['Regular', 'normal'], ['Medium', 'medium'], ['SemiBold', 'semibold']];
let geistFiles = null;
export async function loadGeist() {
  geistFiles ??= (async () => {
    const out = [];
    for (const [file, style] of GEIST) {
      const buf = await (await fetch(`/vendor/fonts/Geist-${file}.ttf?v=2`)).arrayBuffer();
      let bin = '';
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      out.push({ file: `Geist-${file}.ttf`, style, b64: btoa(bin) });
    }
    return out;
  })().catch((e) => { geistFiles = null; throw e; });
  return geistFiles;
}

/** Registers the fonts on `doc` and returns `type(weight, size, color)`, which sets the current text style. */
export function typography(doc, fonts) {
  if (fonts) for (const f of fonts) { doc.addFileToVFS(f.file, f.b64); doc.addFont(f.file, 'Geist', f.style); }
  const FAMILY = fonts ? 'Geist' : 'helvetica';
  const WEIGHT = fonts ? { regular: 'normal', medium: 'medium', semibold: 'semibold' } : { regular: 'normal', medium: 'normal', semibold: 'bold' };
  const type = (weight, size, color) => { doc.setFont(FAMILY, WEIGHT[weight]); doc.setFontSize(size); doc.setTextColor(...color); };
  return { FAMILY, WEIGHT, type };
}

// Rasterises the logo so jsPDF can embed it. Cloudinary is asked for a PNG (jsPDF can't embed webp/avif) and serves CORS headers,
// so the canvas stays readable.
export function loadLogo(url) {
  if (!url) return Promise.resolve(null);
  const src = url.includes('res.cloudinary.com') ? url.replace('/upload/', '/upload/f_png,w_600/') : url;
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), 6000);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      clearTimeout(timer);
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve({ data: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = src;
  });
}
